/**
 * useVendor Hook - Centralized Vendor Management
 * 
 * Provides complete vendor management including:
 * - Current vendor state and configuration
 * - Vendor switching with permission validation
 * - Access to vendor-specific resources (product DB, metrics, theme, rules)
 * - Vendor capability checks
 * - Configuration overrides for vendor switching
 * 
 * @module useVendor
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  getVendor, 
  getAccessibleVendors, 
  hasVendorAccess,
  getVendorProductDb,
  getVendorMetricsDb,
  getVendorTheme,
  getVendorRules,
  getVendorCapabilities,
  getVendorTerms,
  getDefaultVendor
} from '../config/vendorRegistry';
import { log } from '../utils/devLog';

/**
 * useVendor Hook
 *
 * @param {Object} options - Hook options
 * @param {string} [options.initialVendor] - Vendor to start on. ConfiguratorGate passes the
 *   user's first accessible vendor so the very first render — the one
 *   useConfigurationLogic captures its initial config from — is already on the right
 *   vendor. Defaults to the AIC placeholder (getDefaultVendor).
 * @param {string[]} options.userGroups - Cognito groups for the current user (defaults to [])
 * @param {boolean} [options.isCheckingAccess] - Whether permissions are still resolving. When
 *   omitted, falls back to the legacy heuristic (`userGroups.length === 0`) so existing callers
 *   that don't yet know the real value keep their current behaviour.
 * @returns {Object} Vendor state and control functions
 */
export const useVendor = (options = {}) => {
  const { initialVendor = getDefaultVendor(), userGroups = [] } = options;

  // CONF-140: callers that know whether Cognito groups have resolved pass
  // `isCheckingAccess` explicitly. The legacy heuristic (no groups ⇒ still
  // checking) is kept ONLY when the flag is omitted, because it misreads a
  // locked user (legitimately zero groups) as permanently loading — the
  // visible result (default vendor only) happened to coincide, which is why
  // it survived. App.js still omits the flag; the V2 providers pass it.
  const isCheckingAccess = options.isCheckingAccess ?? userGroups.length === 0;

  // Current vendor state
  const [currentVendorId, setCurrentVendorId] = useState(initialVendor);
  
  // Accessible vendors, in registry order. While groups are still resolving the
  // current vendor stands in for the list so nothing downstream sees an empty
  // dropdown flicker.
  const accessibleVendorIds = useMemo(() => {
    if (isCheckingAccess) return [currentVendorId];
    return getAccessibleVendors(userGroups);
  }, [userGroups, isCheckingAccess, currentVendorId]);

  // Snap to the FIRST accessible vendor if the current one is not allowed.
  // Safety net only: ConfiguratorGate hands both shells the right initialVendor,
  // so on a normal mount this is a no-op. With zero accessible vendors there is
  // nothing to snap to; the placeholder stays so vendor/productDb/theme remain
  // defined objects (the gate never mounts a shell in that state).
  useEffect(() => {
    if (isCheckingAccess) return;
    if (accessibleVendorIds.length === 0) return;
    if (!accessibleVendorIds.includes(currentVendorId)) {
      setCurrentVendorId(accessibleVendorIds[0]);
    }
  }, [currentVendorId, accessibleVendorIds, isCheckingAccess]);
  
  // Get current vendor configuration
  const vendor = useMemo(() => {
    return getVendor(currentVendorId);
  }, [currentVendorId]);
  
  // Vendor-specific resources (memoized for performance)
  const productDb = useMemo(() => {
    return getVendorProductDb(currentVendorId);
  }, [currentVendorId]);
  
  const metricsDb = useMemo(() => {
    return getVendorMetricsDb(currentVendorId);
  }, [currentVendorId]);
  
  const theme = useMemo(() => {
    return getVendorTheme(currentVendorId);
  }, [currentVendorId]);
  
  const rules = useMemo(() => {
    return getVendorRules(currentVendorId);
  }, [currentVendorId]);
  
  const capabilities = useMemo(() => {
    return getVendorCapabilities(currentVendorId);
  }, [currentVendorId]);
  
  const terms = useMemo(() => {
    return getVendorTerms(currentVendorId);
  }, [currentVendorId]);
  
  /**
   * Switch to a different vendor
   * @param {string} newVendorId - Target vendor ID
   * @param {Object} options - Switch options
   * @param {boolean} options.force - Force switch even without permission (for admin)
   * @returns {boolean} Success status
   */
  const switchVendor = useCallback((newVendorId, options = {}) => {
    const { force = false } = options;
    
    // Validate vendor ID
    const newVendor = getVendor(newVendorId);
    if (!newVendor || !newVendor.isActive) {
      console.warn(`Invalid or inactive vendor: ${newVendorId}`);
      return false;
    }
    
    // Check access permission
    if (!force && !hasVendorAccess(newVendorId, userGroups)) {
      console.warn(`Access denied to vendor: ${newVendorId}`);
      return false;
    }
    
    log(`🔄 Switching vendor from ${currentVendorId} to ${newVendorId}`);
    
    setCurrentVendorId(newVendorId);
    return true;
  }, [currentVendorId, userGroups]);
  
  /**
   * Toggle to next accessible vendor (for vendor toggle button)
   * @returns {boolean} Success status
   */
  const toggleVendor = useCallback(() => {
    if (accessibleVendorIds.length <= 1) {
      console.warn('User only has access to one vendor, cannot toggle');
      return false;
    }
    
    // Get next accessible vendor
    const currentIndex = accessibleVendorIds.indexOf(currentVendorId);
    const nextIndex = (currentIndex + 1) % accessibleVendorIds.length;
    const nextVendorId = accessibleVendorIds[nextIndex];
    
    return switchVendor(nextVendorId);
  }, [currentVendorId, accessibleVendorIds, switchVendor]);
  
  /**
   * Check if user can switch to a specific vendor
   * @param {string} vendorId - Target vendor ID
   * @returns {boolean} True if switch is allowed
   */
  const canSwitchTo = useCallback((vendorId) => {
    return hasVendorAccess(vendorId, userGroups);
  }, [userGroups]);
  
  /**
   * Check if a specific capability is supported
   * @param {string} capability - Capability name (e.g., 'supportsHybrid')
   * @returns {boolean} True if supported
   */
  const hasCapability = useCallback((capability) => {
    return capabilities[capability] === true;
  }, [capabilities]);
  
  /**
   * Check if a JBOD size is valid for current vendor
   * @param {number} size - JBOD size to validate
   * @returns {boolean} True if valid
   */
  const isValidJbodSize = useCallback((size) => {
    return capabilities.availableJbodSizes.includes(size);
  }, [capabilities]);
  
  /**
   * Check if a spare count is valid for current vendor
   * @param {number} count - Spare count to validate
   * @returns {boolean} True if valid
   */
  const isValidSpareCount = useCallback((count) => {
    return capabilities.availableSpares.includes(count);
  }, [capabilities]);
  
  /**
   * Get configuration overrides for vendor switching
   * Returns config fields that should be updated when switching vendors
   * @param {string} [vendorId] - Optional vendor ID (defaults to current vendor)
   * @returns {Object} Configuration overrides
   */
  const getVendorConfigOverrides = useCallback((vendorId = currentVendorId) => {
    // Fetch the target vendor's config (might be current or different)
    const targetVendor = getVendor(vendorId);
    const targetRules = getVendorRules(vendorId);
    const targetCapabilities = getVendorCapabilities(vendorId);
    
    const overrides = {
      hardwareVendor: vendorId,
      jbodSize: targetCapabilities.defaultJbodSize,
    };
    
    // Force consultation mode if vendor requires it
    if (targetRules.forceConsultationMode) {
      overrides.consultationMode = true;
    }
    
    return overrides;
  }, [currentVendorId]); // Only depends on currentVendorId for the default
  
  /**
   * Get the next vendor ID in the accessible list
   * @returns {string|null} Next vendor ID or null if only one vendor
   */
  const getNextVendorId = useCallback(() => {
    if (accessibleVendorIds.length <= 1) return null;
    
    const currentIndex = accessibleVendorIds.indexOf(currentVendorId);
    const nextIndex = (currentIndex + 1) % accessibleVendorIds.length;
    return accessibleVendorIds[nextIndex];
  }, [currentVendorId, accessibleVendorIds]);
  
  /**
   * Get vendor display info for UI rendering
   * @returns {Object} Display information
   */
  const getDisplayInfo = useCallback(() => {
    return {
      name: vendor.displayName,
      logo: vendor.branding.logo,
      combinedLogo: vendor.branding.combinedLogo,
      logoClassName: vendor.branding.logoClassName
    };
  }, [vendor]);
  
  return {
    // Current vendor state
    vendorId: currentVendorId,
    vendor,
    
    // Vendor resources
    productDb,
    metricsDb,
    theme,
    rules,
    capabilities,
    terms,
    
    // Access control
    accessibleVendorIds,
    canSwitchTo,
    hasMultipleVendors: accessibleVendorIds.length > 1,
    
    // Capability checks
    hasCapability,
    isValidJbodSize,
    isValidSpareCount,
    
    // Vendor switching
    switchVendor,
    toggleVendor,
    getNextVendorId,
    getVendorConfigOverrides,
    
    // Display helpers
    getDisplayInfo,
    
    // Loading state
    isLoading: isCheckingAccess
  };
};

export default useVendor;